import { enTetesAuth } from './enTetesAuth';
/**
 * Themes Service - Handle theme management
 */


const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || 'https://apicabaza.creago.be/rest/cabaza') + '/themes';

/**
 * Handle API response
 */
const handleResponse = async (response) => {
    const text = await response.text();
    let result;
    try {
        result = text ? JSON.parse(text) : {};
    } catch (e) {
        console.error('Failed to parse themes response:', e);
        throw new Error('Invalid response from server');
    }

    if (!response.ok) {
        throw new Error(result.message || result.MESSAGE || `HTTP ${response.status}`);
    }

    return result;
};

/**
 * Get all available themes
 * @param {string} type - Optional filter by theme type (gradient, flat-light, flat-dark, custom)
 * @returns {Promise<Array>} List of themes
 */
export const getThemes = async (type = '') => {
    try {
        const params = new URLSearchParams();
        if (type) params.append('type', type);

        const response = await fetch(`${API_BASE_URL}?${params}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await handleResponse(response);
        const data = result.data || result.DATA || [];

        return data.map(normalizeTheme);
    } catch (error) {
        console.error('Error getting themes:', error);
        return [];
    }
};

/**
 * Get the default theme
 * @returns {Promise<Object|null>} Default theme
 */
export const getDefaultTheme = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/default`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        const result = await handleResponse(response);
        const data = result.data || result.DATA;

        return data ? normalizeTheme(data) : null;
    } catch (error) {
        console.error('Error getting default theme:', error);
        return null;
    }
};

/**
 * Get a theme by ID
 * @param {number} themeId - Theme ID
 * @returns {Promise<Object|null>} Theme object
 */
export const getThemeById = async (themeId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/${themeId}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await handleResponse(response);
        const data = result.data || result.DATA;

        return data ? normalizeTheme(data) : null;
    } catch (error) {
        console.error('Error getting theme:', error);
        return null;
    }
};

/**
 * Create a custom theme
 * @param {Object} themeData - Theme data
 * @returns {Promise<Object>} Result with success and themeId
 */
export const createTheme = async (themeData) => {
    try {
        const response = await fetch(API_BASE_URL, {
            method: 'POST',
            credentials: 'include',
            headers: enTetesAuth(),
            body: JSON.stringify(themeData)
        });

        const result = await handleResponse(response);

        return {
            success: result.success || result.SUCCESS,
            themeId: result.data?.id || result.DATA?.id,
            message: result.message || result.MESSAGE
        };
    } catch (error) {
        console.error('Error creating theme:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Delete a custom theme
 * @param {number} themeId - Theme ID
 * @returns {Promise<Object>} Result
 */
export const deleteTheme = async (themeId) => {
    try {
        const userId = localStorage.getItem('userId');
        const response = await fetch(`${API_BASE_URL}/${themeId}?userId=${userId}`, {
            method: 'DELETE',
            headers: enTetesAuth(false),
            credentials: 'include'
        });

        const result = await handleResponse(response);

        return {
            success: result.success || result.SUCCESS,
            message: result.message || result.MESSAGE
        };
    } catch (error) {
        console.error('Error deleting theme:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Note: User preferences are stored in localStorage (not in DB)
 * This keeps things simple and allows offline theme switching
 */

/**
 * Normalize theme data from API
 */
const normalizeTheme = (theme) => ({
    id: theme.id || theme.ID,
    name: theme.name || theme.NAME,
    nameFr: theme.name_fr || theme.NAME_FR,
    nameEn: theme.name_en || theme.NAME_EN,
    themeType: theme.themetype || theme.themeType || theme.THEMETYPE,
    primaryColor: theme.primarycolor || theme.primaryColor || theme.PRIMARYCOLOR,
    secondaryColor: theme.secondarycolor || theme.secondaryColor || theme.SECONDARYCOLOR,
    accentColor: theme.accentcolor || theme.accentColor || theme.ACCENTCOLOR,
    backgroundColor: theme.backgroundcolor || theme.backgroundColor || theme.BACKGROUNDCOLOR,
    surfaceColor: theme.surfacecolor || theme.surfaceColor || theme.SURFACECOLOR,
    textPrimary: theme.textprimary || theme.textPrimary || theme.TEXTPRIMARY,
    textSecondary: theme.textsecondary || theme.textSecondary || theme.TEXTSECONDARY,
    textOnPrimary: theme.textonprimary || theme.textOnPrimary || theme.TEXTONPRIMARY,
    borderColor: theme.bordercolor || theme.borderColor || theme.BORDERCOLOR,
    successColor: theme.successcolor || theme.successColor || theme.SUCCESSCOLOR,
    warningColor: theme.warningcolor || theme.warningColor || theme.WARNINGCOLOR,
    errorColor: theme.errorcolor || theme.errorColor || theme.ERRORCOLOR,
    sidebarBg: theme.sidebarbg || theme.sidebarBg || theme.SIDEBARBG,
    sidebarText: theme.sidebartext || theme.sidebarText || theme.SIDEBARTEXT,
    sidebarActiveText: theme.sidebaractivetext || theme.sidebarActiveText || theme.SIDEBARACTIVETEXT,
    sidebarBgImage: theme.sidebarbgimage || theme.sidebarBgImage || theme.SIDEBARBGIMAGE,
    sidebarBgOverlay: theme.sidebarbgoverlay || theme.sidebarBgOverlay || theme.SIDEBARBGOVERLAY,
    isSystem: theme.issystem || theme.isSystem || theme.ISSYSTEM,
    isDefault: theme.isdefault || theme.isDefault || theme.ISDEFAULT,
    sortOrder: theme.sortorder || theme.sortOrder || theme.SORTORDER,
    // New custom theme fields
    visibility: theme.visibility || theme.VISIBILITY || 'private',
    imageSource: theme.imagesource || theme.imageSource || theme.IMAGESOURCE,
    serverImagePath: theme.serverimagepath || theme.serverImagePath || theme.SERVERIMAGEPATH,
    likesCount: theme.likescount || theme.likesCount || theme.LIKESCOUNT || 0,
    downloadsCount: theme.downloadscount || theme.downloadsCount || theme.DOWNLOADSCOUNT || 0,
    createdById: theme.createdbyid || theme.createdById || theme.CREATEDBYID,
    creatorName: theme.creatorname || theme.creatorName || theme.CREATORNAME,
    isOwner: theme.isowner || theme.isOwner || theme.ISOWNER || false
});

/**
 * Apply theme CSS variables to the document
 * @param {Object} theme - Theme object
 */
export const applyTheme = (theme) => {
    if (!theme) return;

    const root = document.documentElement;

    // Primary colors
    root.style.setProperty('--color-cabaza-purple', theme.primaryColor);
    root.style.setProperty('--color-cabaza-pink', theme.secondaryColor || theme.primaryColor);

    // Background colors
    root.style.setProperty('--color-background', theme.backgroundColor);
    root.style.setProperty('--color-surface', theme.surfaceColor);

    // Text colors
    root.style.setProperty('--color-text-primary', theme.textPrimary);
    root.style.setProperty('--color-text-secondary', theme.textSecondary);
    root.style.setProperty('--color-text-on-primary', theme.textOnPrimary);

    // Border color
    root.style.setProperty('--color-border', theme.borderColor);

    // Sidebar colors
    if (theme.sidebarBg) {
        root.style.setProperty('--color-sidebar-bg', theme.sidebarBg);
    } else {
        root.style.removeProperty('--color-sidebar-bg');
    }
    if (theme.sidebarText) {
        root.style.setProperty('--color-sidebar-text', theme.sidebarText);
    } else {
        root.style.removeProperty('--color-sidebar-text');
    }

    // Sidebar background image (for image-bg themes)
    if (theme.sidebarBgImage) {
        root.style.setProperty('--sidebar-bg-image', `url(${theme.sidebarBgImage})`);
        root.style.setProperty('--sidebar-bg-overlay', theme.sidebarBgOverlay || 'rgba(0,0,0,0.4)');
        document.body.classList.add('image-bg-theme');
    } else {
        root.style.removeProperty('--sidebar-bg-image');
        root.style.removeProperty('--sidebar-bg-overlay');
        document.body.classList.remove('image-bg-theme');
    }

    // Apply dark mode class if needed
    if (theme.themeType === 'flat-dark' || theme.themeType === 'image-bg') {
        document.body.classList.add('dark-theme');
        document.body.style.backgroundColor = theme.backgroundColor;
        document.body.style.color = theme.textPrimary;
    } else {
        document.body.classList.remove('dark-theme');
        document.body.style.backgroundColor = '';
        document.body.style.color = '';
    }

    // Store theme in localStorage for persistence
    localStorage.setItem('currentTheme', JSON.stringify(theme));
};

/**
 * Get theme name in current language
 * @param {Object} theme - Theme object
 * @param {string} lang - Language code
 * @returns {string} Theme name
 */
export const getThemeName = (theme, lang = 'fr') => {
    if (!theme) return '';
    if (lang === 'fr' && theme.nameFr) return theme.nameFr;
    if (lang === 'en' && theme.nameEn) return theme.nameEn;
    return theme.name || '';
};

/**
 * Get stored theme from localStorage
 * @returns {Object|null} Stored theme
 */
export const getStoredTheme = () => {
    try {
        const stored = localStorage.getItem('currentTheme');
        return stored ? JSON.parse(stored) : null;
    } catch {
        return null;
    }
};

/**
 * Clear stored theme
 */
export const clearStoredTheme = () => {
    localStorage.removeItem('currentTheme');
};

/**
 * Get community themes (public themes from other users)
 * @returns {Promise<Array>} List of community themes
 */
export const getCommunityThemes = async () => {
    try {
        const userId = localStorage.getItem('userId');
        const response = await fetch(`${API_BASE_URL}/community?userId=${userId}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await handleResponse(response);
        const data = result.data || result.DATA || [];

        return data.map(normalizeTheme);
    } catch (error) {
        console.error('Error getting community themes:', error);
        return [];
    }
};

/**
 * Get user's own themes
 * @returns {Promise<Array>} List of user's themes
 */
export const getMyThemes = async () => {
    try {
        const userId = localStorage.getItem('userId');
        const response = await fetch(`${API_BASE_URL}/mine?userId=${userId}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await handleResponse(response);
        const data = result.data || result.DATA || [];
        return data.map(normalizeTheme);
    } catch (error) {
        console.error('Error getting my themes:', error);
        return [];
    }
};

/**
 * Update an existing theme
 * @param {number} themeId - Theme ID
 * @param {Object} themeData - Updated theme data
 * @returns {Promise<Object>} Result
 */
export const updateTheme = async (themeId, themeData) => {
    try {
        const response = await fetch(`${API_BASE_URL}/${themeId}`, {
            method: 'PUT',
            headers: enTetesAuth(false),
            credentials: 'include',
            body: JSON.stringify(themeData)
        });

        const result = await handleResponse(response);

        return {
            success: result.success || result.SUCCESS,
            message: result.message || result.MESSAGE
        };
    } catch (error) {
        console.error('Error updating theme:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Like or unlike a theme
 * @param {number} themeId - Theme ID
 * @returns {Promise<Object>} Result with liked status and new count
 */
export const likeTheme = async (themeId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/${themeId}/like`, {
            method: 'POST',
            headers: enTetesAuth(false),
            credentials: 'include'
        });

        const result = await handleResponse(response);

        return {
            success: result.success || result.SUCCESS,
            liked: result.data?.liked || result.DATA?.liked,
            likesCount: result.data?.likesCount || result.DATA?.likesCount,
            message: result.message || result.MESSAGE
        };
    } catch (error) {
        console.error('Error liking theme:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Track theme download
 * @param {number} themeId - Theme ID
 * @returns {Promise<Object>} Result
 */
export const trackDownload = async (themeId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/${themeId}/download`, {
            method: 'POST',
            headers: enTetesAuth(false),
            credentials: 'include'
        });

        const result = await handleResponse(response);

        return {
            success: result.success || result.SUCCESS,
            downloadsCount: result.data?.downloadsCount || result.DATA?.downloadsCount
        };
    } catch (error) {
        console.error('Error tracking download:', error);
        return { success: false };
    }
};

/**
 * Upload a theme image
 * @param {File} file - Image file to upload
 * @returns {Promise<Object>} Result with image URL
 */
export const uploadThemeImage = async (file) => {
    try {
        const userId = localStorage.getItem('userId');
        const formData = new FormData();
        formData.append('image', file);

        // enTetesAuth(false) REND EXACTEMENT CE QU'IL FAUT ICI : le jeton
        // Authorization, et PAS de Content-Type — le navigateur doit poser
        // lui-meme la frontiere multipart.
        //
        // Il y avait ici `const headers = {}` suivi d'un delete sur une cle
        // qui n'existait pas : l'objet partait vide, donc sans jeton. Seule
        // l'identite lue dans localStorage designait l'appelant, et le client
        // la controle. Trouve par atteignabilite.test.js des son premier
        // passage.
        const response = await fetch(`${API_BASE_URL}/upload-image?userId=${userId}`, {
            method: 'POST',
            headers: enTetesAuth(false),
            credentials: 'include',
            body: formData
        });

        const result = await handleResponse(response);

        // Build full URL from VITE_FILES_BASE_URL + serverPath
        const serverPath = result.serverPath || result.SERVERPATH;
        const filesBaseUrl = import.meta.env.VITE_FILES_BASE_URL || '';
        const imageUrl = serverPath ? `${filesBaseUrl}${serverPath}` : null;

        return {
            success: result.success || result.SUCCESS,
            imageUrl: imageUrl,
            serverPath: serverPath,
            message: result.message || result.MESSAGE
        };
    } catch (error) {
        console.error('Error uploading theme image:', error);
        return { success: false, message: error.message };
    }
};
